import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import jwt_decode from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  constructor(private _translateService: TranslateService,
              private _oauthService: OAuthService) {
  }

  /**
   * Determines the user's language preference and sets it for the application.
   *
   * The method prioritizes the language set in the user's JWT ID token.
   * If the token or a locale within it is not available, it falls back to the browser's
   * reported language. If the browser's language is not supported, it defaults to 'en'.
   *
   * @param truncateLang If true, returns only the two-letter language code (e.g., 'pt')
   *                     instead of the full locale (e.g., 'pt-BR'). This is useful for cases
   *                     where a generic language key is needed, independent of the i18n system.
   *                     Defaults to false.
   * @returns The determined language code as a string (e.g., 'en', 'fr', 'pt', 'pt-BR').
   */
  public getLanguage(truncateLang = false): string {
    const idToken: any = this._oauthService.getIdToken();

    if (idToken) {
      const decodedToken: any = jwt_decode(idToken);
      if (decodedToken.locale && decodedToken.locale !== '') {
        const locale = decodedToken.locale;
        this._translateService.use(locale);
        return truncateLang ? locale.slice(0, 2) : locale;
      } else {
        const browserLang = navigator.language;
        // If a browser language is not supported, use 'en'.
        if (browserLang !== 'en' && browserLang !== 'fr' && browserLang !== 'pt' && browserLang !== 'pt-BR') {
          return 'en';
        }
        return truncateLang ? browserLang.slice(0, 2) : browserLang;
      }
    }
    return 'en';
  }
}
